import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useLocation, useNavigate } from "react-router";
import toast, { Toaster } from "react-hot-toast";

import IzunefeshLogo from "../../components/logo";
import { fetcher, headers } from "../../network";
import { API } from "../../network/api";
import { CONSTANTS } from "../../constants";
import { registerNotification } from "../../utils/push";

const setupTwoFactorQuery = async (homeId: string) => {
  const { data } = await fetcher().post<{ qrCodeURL: string }>(
    API.POST.SETUP_2FA,
    {
      homeId,
    }
  );

  return data;
};

const verifyTwoFactorMutation = async (code: string) => {
  const { data } = await fetcher().post<{ token: string }>(
    API.POST.VERIFY_2FA,
    {
      token: code,
    }
  );

  return data;
};

const TwoFactorAuthPage = () => {
  const { homeId, is2FAEnabled } = useLocation().state as {
    homeId: string;
    is2FAEnabled: boolean;
  };

  const [isQRCodeScanned, setIsQRCodeScanned] = useState<boolean>(is2FAEnabled);
  const [code, setCode] = useState<string[]>(Array(6).fill(""));
  const [isLastFromSixDigitError, setIsLastFromSixDigitError] = useState(false);
  const inputsRef = useRef<HTMLInputElement[]>([]);

  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["twoFactorAuth", "setup"],
    queryFn: () => setupTwoFactorQuery(homeId),
    enabled: !is2FAEnabled,
  });

  const { mutate } = useMutation({
    mutationFn: verifyTwoFactorMutation,
    onSuccess: ({ token }) => {
      headers.authorization = `Bearer ${token}`;
      registerNotification(homeId);
      navigate(CONSTANTS.ROUTES.home, { state: { homeId }, replace: true });
    },
    onError: () => {
      toast.error("סיסמא שגויה או שאין גישה");
    },
  });

  // Set focus on the leftmost input box (RTL logic: last box)
  useEffect(() => {
    if (isQRCodeScanned) {
      inputsRef.current[5]?.focus(); // Focus the leftmost box (last index)
    }
  }, [isQRCodeScanned, inputsRef.current]);

  // Handle input changes
  const handleChange = (value: string, index: number) => {
    if (isNaN(Number(value))) return; // Only allow numbers
    const updatedCode = [...code];
    updatedCode[index] = value;
    setCode(updatedCode);

    // Move to the next input box if the current box has a value
    if (value && index > 0) {
      const nextInput = inputsRef.current[index - 1];
      nextInput?.focus();
    }

    setIsLastFromSixDigitError(false);
  };

  // Handle key down for deletion and navigation
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace") {
      // Clear the current input and move to the next one (RTL behavior)
      if (code[index] === "" && index < 5) {
        const nextInput = inputsRef.current[index + 1];
        nextInput?.focus();
      } else {
        const updatedCode = [...code];
        updatedCode[index] = "";
        setCode(updatedCode);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      // Move to the next input (visually to the right in RTL)
      const prevInput = inputsRef.current[index - 1];
      prevInput?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      // Move to the previous input (visually to the left in RTL)
      const nextInput = inputsRef.current[index + 1];
      nextInput?.focus();
    } else if (e.key === "Enter") {
      // Trigger the submit function when Enter is pressed
      handleSubmit();
    }
  };

  // Handle QR code scanning
  const handleQRCodeScanned = () => {
    setIsQRCodeScanned(true);
  };

  // Submit the 2FA code
  const handleSubmit = () => {
    if (code.length === 6) {
      const theCode = [...code].reverse().join("");
      mutate(theCode);
    } else {
      setIsLastFromSixDigitError(true);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-2 sm:p-6">
      <div className="w-full max-w-md bg-white shadow-lg rounded-lg p-8">
        <IzunefeshLogo notClickable />
        <h1 className="text-2xl font-bold text-center mb-4">
          אימות דו-שלבי (2FA)
        </h1>
        {!isQRCodeScanned ? (
          <div className="text-center flex flex-col items-center">
            <p className="text-gray-600 mb-4">
              סרוק את ה-QR קוד באמצעות אפליקצית Google Authenticator כדי להגדיר
              את האימות הדו-שלבי.
            </p>
            {/* QR Code Placeholder */}
            <div className="flex justify-center items-center h-48 w-48 border-2 border-dashed border-gray-400 rounded-md mb-6">
              {isLoading || !data ? (
                <Skeleton count={1} height={48} width={48} />
              ) : (
                <img src={data.qrCodeURL} alt="2fa qr code url" />
              )}
            </div>
            <button
              disabled={isLoading}
              onClick={handleQRCodeScanned}
              className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded">
              סרקתי את הקוד
            </button>
          </div>
        ) : (
          <div className="text-center">
            <p className="text-gray-600 mb-4">
              הזן את הקוד בן 6 הספרות שהתקבלו באפליקציה שלך.
            </p>
            <div className="flex justify-between mb-6">
              {code.map((digit, index) => (
                <input
                  key={index}
                  id={`digit-${index}`}
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  ref={(el) => (inputsRef.current[index] = el!)} // Store input ref
                  className={`w-10 h-10 sm:w-12 sm:h-12  text-center text-2xl font-bold border-2 border-gray-300 rounded focus:outline-none focus:ring-2 ${
                    isLastFromSixDigitError
                      ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  }`}
                />
              ))}
            </div>
            <button
              onClick={handleSubmit}
              className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded">
              שלח קוד
            </button>
          </div>
        )}
      </div>
      <Toaster position="bottom-center" />
    </div>
  );
};

export default TwoFactorAuthPage;
